















































































































import { Component } from "vue-property-decorator";
import MiraBarRowComponent from "@/components/mira/MiraBarRow.vue";
import MiraDetailCardComponent from "@/components/mira/MiraDetailCard.vue";
import MiraFactsComponent from "@/components/mira/MiraFacts.vue";
import MiraKpiTileComponent from "@/components/mira/MiraKpiTile.vue";
import MiraKpiTileGridComponent from "@/components/mira/MiraKpiTileGrid.vue";
import MiraComparisonCardComponent from "@/components/mira/MiraComparisonCard.vue";
import MiraScreenBase from "@/components/mira/MiraScreenBase";
import MiraScreenHeaderComponent from "@/components/mira/MiraScreenHeader.vue";
import MiraTrendCardComponent from "@/components/mira/MiraTrendCard.vue";
import MiraStackedTrendCardComponent from "@/components/mira/MiraStackedTrendCard.vue";
import MiraUnlockListComponent from "@/components/mira/MiraUnlockList.vue";
import { MiraSection } from "@/data/models/Mira";
import { CATEGORY_UNKNOWN_ID, createDefaultDeviceCategories } from "@/data/models/DeviceSettingsManager";
import { getClientsByIds, getMemberClientIds } from "@/data/repository/ClientRepository";
import { getDeviceCategoriesOfClient } from "@/data/repository/SettingsRepository";
import { defineCategoriesAndTemplatesClientAccess } from "@/permissions/ClientPermissions";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import { MiraSectionUnlocks, collectUnlocks } from "@/utils/MiraDataQuality";
import { MiraBar, MiraTile } from "@/utils/MiraDataQualityView";
import {
  buildCategoryRows,
  buildDeviceTiles,
  categoryNames,
  collectMemberCategoryNames,
  devicesTrendFormatter,
  energyFacts,
  operatingHourFacts,
} from "@/utils/MiraDevicesView";
import { MiraFact } from "@/utils/MiraDryingView";

/**
 * Mira Geräte: fleet utilisation, power consumption, device days, devices per project, operating hours,
 * open assignments on finished projects, and the device days per category.
 *
 * Built like Trocknung (`MiraScreenBase` for loading, the period picker and the header). One
 * Flottenauslastung tile per fleet (#2610072) and energy in kWh (Taimoor, 2026-10-01), both enforced in
 * `@/utils/MiraDevicesView`. A client without projects gets only fleet utilisation, because the other
 * figures cannot apply to it. See `docs/analytics/mira-office-screens.md`.
 */
@Component({
  components: {
    "mira-bar-row": MiraBarRowComponent,
    "mira-detail-card": MiraDetailCardComponent,
    "mira-facts": MiraFactsComponent,
    "mira-kpi-tile": MiraKpiTileComponent,
    "mira-kpi-tile-grid": MiraKpiTileGridComponent,
    "mira-screen-header": MiraScreenHeaderComponent,
    "mira-comparison-card": MiraComparisonCardComponent,
    "mira-trend-card": MiraTrendCardComponent,
    "mira-stacked-trend-card": MiraStackedTrendCardComponent,
    "mira-unlock-list": MiraUnlockListComponent,
  },
})
export default class MiraDevicesComponent extends MiraScreenBase {
  get section(): MiraSection {
    return MiraSection.DEVICES;
  }

  get titleKey(): string {
    return "mira.devices.title";
  }

  get tiles(): MiraTile[] {
    return buildDeviceTiles(this.period, this.comparison, this.viewContext);
  }

  /**
   * The members' category names by id on a group master: the group's category mix counts the members'
   * devices, whose category ids the signed-in client's own settings do not hold. Loaded once on mount,
   * only on a master. See {@link categoryNames} for how they merge with the client's own names.
   */
  memberCategoryNames: Record<string, string> = {};

  async mounted() {
    if (!this.isGroupMaster) {
      return;
    }
    try {
      const members = await getClientsByIds(getMemberClientIds());
      // Only active members, in member-list order so the first name wins deterministically.
      const memberIds = getMemberClientIds().filter((memberId) => members.has(memberId));
      this.memberCategoryNames = await collectMemberCategoryNames(memberIds, getDeviceCategoriesOfClient, [CATEGORY_UNKNOWN_ID]);
    } catch (error) {
      // An unreadable member list leaves the members' categories unnamed; the client's own categories
      // and the defaults still name the rest.
    }
  }

  /**
   * The client's category names, from the settings in force by the same rule the device screens use
   * (own settings, or the master's shared ones). Scaffold keeps both in the store, so nothing is loaded
   * here; the built-in categories go first so a stored name wins over its default.
   *
   * On a group master the members' names (loaded on mount) fill the ids the client's own settings
   * cannot name; the client's own naming and the built-in defaults win where an id is shared, so the
   * default categories always keep their names.
   */
  get categoryNames(): Record<string, string> {
    const settings = defineCategoriesAndTemplatesClientAccess()
      ? DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates
      : DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;

    const ownNames = categoryNames([...createDefaultDeviceCategories(), ...(settings?.deviceCategories ?? [])], [CATEGORY_UNKNOWN_ID]);
    return { ...this.memberCategoryNames, ...ownNames };
  }

  get categoryRows(): MiraBar[] {
    return buildCategoryRows(this.period, this.categoryNames, this.viewContext);
  }

  get energyFacts(): MiraFact[] {
    return energyFacts(this.period, this.viewContext);
  }

  get operatingHourFacts(): MiraFact[] {
    return operatingHourFacts(this.period, this.viewContext);
  }

  /**
   * Only this section's figures waiting on data: the other sections have their own screens. Computed
   * here rather than through `super`, which vue-class-component's computed getters do not support.
   */
  get unlocks(): MiraSectionUnlocks[] {
    return collectUnlocks(this.sections, this.selectedPeriod).filter((group) => group.section === MiraSection.DEVICES);
  }

  trendFormatter(metricId: string): (value: number) => string {
    return devicesTrendFormatter(metricId, this.viewContext);
  }
}
